import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PhysicianWelcomeScreen from '@medflex/components/stories/PhysicianWelcomeScreen';
import GDPRLinks from '@medflex/components/stories/GDPRLinks';
import { trackEvent } from '@medflex/utils/Tracking';
import isGroupChatId from '@medflex/utils/isGroupChatId';
import isWebView from '@medflex/utils/isWebView';
import { GroupIcon } from '@medflex/components/stories/GroupAvatar';
import createParticipantsString from '@medflex/utils/createParticipantsString';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import useFeatures from '../../hooks/useFeatures';
import { STATUS } from '../../store/models/subscription';
import useInvitationRequests from '../../hooks/useInvitationRequests';
import usePermissions from '../../hooks/usePermissions';
import useUserProfile from '../../hooks/useUserProfile';
import useUnreadChats from '../../hooks/useUnreadChats';
import useSupportContact from '../../hooks/useSupportContact';
import useModal from '../../hooks/useModal';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import VideoBox from './VideoBox';
import DemoBox from './DemoBox';
import useSubscription from '../../hooks/useSubscription';
import { MODALS } from '../../hooks/useIsModal';

const PhysicianWelcomeScreenModule = () => {
  const { id: supportPatientContactId } = useSupportContact();
  const {
    chats: unreadChats,
    total: totalUnreadContactMessages,
    remaining: remainingUnreadContactMessages,
  } = useUnreadChats({ load: true });
  const history = useHistory();
  const { open } = useModal();
  const { invitationRequests, invite, ignore } = useInvitationRequests();
  const { hasPatientAccess, hasPhysicianAccess } = usePermissions();
  const { userProfile } = useUserProfile();
  const { pairedPatients, pairedPhysicians } = useAddressBookContacts();
  const [physicianStatusFeature] = useFeatures([FEATURE_KEYS.PHYSICIAN_STATUS]);
  const { status: subscriptionStatus } = useSubscription();

  const pairedContactsCount = useMemo(
    () => pairedPatients.length + pairedPhysicians.length,
    [pairedPatients, pairedPhysicians],
  );

  const chats = useMemo(() => {
    if (userProfile) {
      return unreadChats.map(({ lastMessage, unread: count, id, title, participants }) => {
        const { sender, text, deletedAt, files } = lastMessage;
        const isDeleted = !!deletedAt;
        return {
          id,
          count,
          text,
          title:
            title ||
            (participants && createParticipantsString(userProfile.id)(participants)) ||
            sender.fullNameWithTitle,
          ...(isGroupChatId(id) && { subtitle: sender.fullNameWithTitle }),
          avatar: isGroupChatId(id) ? <GroupIcon /> : sender.avatar,
          initials: sender.initials,
          isDeleted,
          ...(files && { files }),
        };
      });
    }
    return [];
  }, [unreadChats, userProfile]);

  const handleClickChat = ({ id }, event) => {
    history.push(`/chat/${id}`);
    trackEvent(event, { actionText: 'spezifischen Chat öffnen' });
  };

  const handleClickChats = event => {
    if (totalUnreadContactMessages === 0) {
      open({ modal: 'newMessage' });
      trackEvent(event, { actionText: 'Neue Nachricht Modal öffnen' });
    } else {
      history.push('/chat');
      trackEvent(event, { actionText: 'Adressbuch öffnen' });
    }
  };

  const handleClickSupport = event => {
    history.push(`/chat/${supportPatientContactId}`);
    trackEvent(event);
  };

  const handleClickEditStatus = event => {
    trackEvent(event);

    open({ modal: MODALS.status });
  };

  const handleClickInvitationRequests = event => {
    history.push('/invitation-requests');
    trackEvent(event);
  };

  const handleShareWithContacts = event => {
    const shareAppMessageText =
      'Schon gesehen?\nmedflex ist jetzt auch als  App für Smartphone, Tablet und Desktop verfügbar!\n\nDie App bietet neben allen gewohnten Funktionen auch Neues, wie z.B. Push-Mitteilungen.\n\nSmartphone/Tablet App hier herunterladen:\n\nApple App Store\nhttps://apps.apple.com/de/app/medflex/id1604366389\n\nGoogle Play Store\nhttps://play.google.com/store/apps/details?id=de.medflex.app.flutter\n\n\nDesktop App hier herunterladen:\n\nFür Windows und Mac\nhttps://www.medflex.de/desktop-app';
    open({ modal: 'multicast', state: { content: shareAppMessageText } });
    trackEvent(event);
  };

  return (
    <PhysicianWelcomeScreen>
      {(totalUnreadContactMessages || pairedContactsCount > 0) && (
        <PhysicianWelcomeScreen.UnreadChats
          total={totalUnreadContactMessages}
          remaining={remainingUnreadContactMessages}
          chats={chats}
          onClickChat={handleClickChat}
          onClickChats={handleClickChats}
        />
      )}
      {hasPatientAccess && (
        <PhysicianWelcomeScreen.PatientInvitationRequests
          onInvite={invite}
          onDelete={ignore}
          invitationRequests={invitationRequests}
          onClick={handleClickInvitationRequests}
        />
      )}
      <DemoBox />
      {subscriptionStatus === STATUS.LOADED && physicianStatusFeature.active && (
        <PhysicianWelcomeScreen.Status
          onClick={handleClickEditStatus}
          status={physicianStatusFeature.active ? userProfile.attributes.status : null}
          statusSetAt={userProfile.attributes.statusSetAt}
        />
      )}
      {hasPatientAccess && hasPhysicianAccess && !isWebView() && (
        <PhysicianWelcomeScreen.News onShareWithContacts={handleShareWithContacts} />
      )}
      <VideoBox />
      <PhysicianWelcomeScreen.Support onClickSupport={handleClickSupport} />
      <div className="xl:hidden">
        <GDPRLinks />
      </div>
    </PhysicianWelcomeScreen>
  );
};

export default PhysicianWelcomeScreenModule;
